/**
 * Theme (light/dark) selection.
 *
 * `:root` in styles.css defines the light palette and a `prefers-color-scheme: dark` media query
 * flips it — that stays the default. Setting `data-theme="light" | "dark"` on <html> overrides the
 * media query in *both* directions, which is what the manual toggle does. The choice is persisted
 * in localStorage; "system" (no attribute) is the initial state until the user picks a side.
 */

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'naulon.theme';
/** Must match --bg in each palette — used for the browser/PWA status bar colour. */
const BACKGROUND: Record<'light' | 'dark', string> = { light: '#EDE9F4', dark: '#120A1E' };
const MODE_ORDER: ThemeMode[] = ['system', 'light', 'dark'];
const MODE_LABEL: Record<ThemeMode, string> = { system: 'System', light: 'Light', dark: 'Dark' };

const toggles = new Set<HTMLButtonElement>();
let mode: ThemeMode = readStoredMode();

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* private mode / storage disabled — fall back to following the system */
  }
  return 'system';
}

function darkMediaQuery(): MediaQueryList | null {
  return typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
}

/** The theme actually in effect right now, resolving "system" against the OS setting. */
export function effectiveTheme(): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return darkMediaQuery()?.matches ? 'dark' : 'light';
}

export function getThemeMode(): ThemeMode {
  return mode;
}

export function setThemeMode(next: ThemeMode): void {
  mode = next;
  try {
    if (next === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* persistence is best-effort */
  }
  applyTheme();
}

function applyTheme(): void {
  const root = document.documentElement;
  if (mode === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);

  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', BACKGROUND[effectiveTheme()]);

  for (const button of toggles) paintToggle(button);
}

/** Applies the stored choice and keeps "system" live if the OS setting changes mid-session. */
export function initTheme(): void {
  applyTheme();
  darkMediaQuery()?.addEventListener('change', () => {
    if (mode === 'system') applyTheme();
  });
}

const ICON: Record<'light' | 'dark' | 'system', string> = {
  // Sun
  light:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2.6M12 18.9v2.6M2.5 12h2.6M18.9 12h2.6M5.2 5.2l1.9 1.9M16.9 16.9l1.9 1.9M18.8 5.2l-1.9 1.9M7.1 16.9l-1.9 1.9"/></svg>',
  // Moon
  dark: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.2 8.2 0 1 0 10.2 10.2Z"/></svg>',
  // Half-filled circle
  system: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.2"/><path d="M12 3.8v16.4a8.2 8.2 0 0 0 0-16.4Z" class="fill"/></svg>',
};

function paintToggle(button: HTMLButtonElement): void {
  const icon = mode === 'system' ? ICON.system : ICON[mode];
  const suffix = mode === 'system' ? ` (${MODE_LABEL[effectiveTheme()]})` : '';
  button.innerHTML = `<span class="theme-toggle-icon">${icon}</span><span class="theme-toggle-label">${MODE_LABEL[mode]}${suffix}</span>`;
  button.title = `Appearance: ${MODE_LABEL[mode]}${suffix} — click to switch`;
  button.setAttribute('aria-label', `Appearance: ${MODE_LABEL[mode]}${suffix}. Click to switch.`);
}

/**
 * A button that cycles System → Light → Dark. Every toggle created here stays in sync, so the one
 * in the nav rail and the one in the account panel never disagree.
 */
export function createThemeToggle(): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'theme-toggle';
  button.addEventListener('click', () => {
    const next = MODE_ORDER[(MODE_ORDER.indexOf(mode) + 1) % MODE_ORDER.length];
    setThemeMode(next);
  });
  toggles.add(button);
  paintToggle(button);
  return button;
}
